
import EventBus from '_utils/EventBus';
import GaHelper from '_utils/GaHelper';
import StringHelper from '_utils/StringHelper';

document.addEventListener("DOMContentLoaded", () => {
  logInPage.wire();
  logInPage.trackLoginInitiated();
});


const logInPage = {
  /*
  ==================================================
    WIRE UI ELEMENTS
  ==================================================
  */

  wire() {
    document.querySelector('.google-login-button')?.addEventListener('click', () => this.handleSocialLoginRequest('google'));

    document.querySelector('.apple-login-button')?.addEventListener('click', () => this.handleSocialLoginRequest('apple'));

    document.querySelector('#log-in-form')?.addEventListener('submit', (e) => this.handleEmailLoginRequest(e));
    document.querySelector('.pill-button')?.addEventListener('click', () => this.handleEmailLoginRequest());
  },


  /*
  ==================================================
    EVENT HANDLERS
  ==================================================
  */

  handleEmailLoginRequest(e) {
    if (e) {
      e.preventDefault();
    }

    const validation = this.validateInputCredentials();

    if (validation.isValid) {
      this.submitEmailLogInRequest();
    } else {
      this.showErrorMessage({ identifier: validation.identifier, message: validation.message });
    }
  },

  handleSocialLoginRequest(provider) {
    this.presetLoginSessionData()
      .then(() => this.submitSocialLogInRequest(provider))
      .catch((err) => {
        console.error(err);
        this.showErrorMessage({ identifier: null, message: 'Something went wrong. Please try again.' });
      });
  },  


  /*
  ==================================================
    LOCAL UTILS
  ==================================================
  */

  checkJsonResponse(rs) {
    if (rs.ok) return rs.json();

    const ct = rs.headers.get('content-type');

    if (ct && ct.indexOf('json') >= 0) {
      return rs.json().then((data) => { throw new Error(data.error); });
    } else if (ct && ct.indexOf('html') >= 0) {
      throw new Error('Unknown Error (code: 740)');
    } else {
      return rs.text().then((data) => { throw new Error(data); });
    }
  },

  clearErrorState() {
    document.querySelector('.error-field').innerHTML = '';
    document.querySelector('.error')?.classList.remove('error');
  },

  getParamsFromContextData() {
    const contextData = document.getElementById('auth-log-in-page')?.getAttribute('data') || '{}';
    return JSON.parse(contextData);
  },

  initFor() {
    const params = this.getParamsFromContextData();
    
    if (params?.source) {
      return `initiated-${params.source}`
    } else {
      return 'initiated';
    }
  },
  
  parseDelay(token) {
    if (!token) return null;

    const i = token.lastIndexOf('=');
    if (i < 0 || i >= token.length - 1) return null;

    return parseInt(token.substring(i + 1, token.length));
  },

  presetLoginSessionData(){
    return new Promise((resolve, reject) => {
      const headers = { 'Content-Type': 'application/json' };
      const params = this.getParamsFromContextData();

      const {catId, packId, returnTo, source} = params;

      if (!(catId || packId || returnTo || source)) {
        resolve();
        return;
      }

      const url = '/api/sessions/pre';
      const body = JSON.stringify(params);
      const opts = { body, headers, method: 'POST' };

      fetch(url, opts)
        .then((rs) => this.checkJsonResponse(rs))
        .then(() =>  resolve('ok'))
        .catch((err) => {
          this.showErrorMessage(err);
          reject(err);
        });
    });
  },

  showErrorMessage(err) {
    this.clearErrorState();

    if (!err || !err.message) {
      return null;
    }

    if (err) {
      if (err.identifier) {
        document.getElementById(err.identifier).classList.add('error');
      }

      const errorField = document.querySelector('.error-field');
      const errorMsg = document.createElement('span');
      errorMsg.classList.add('error-text');
      errorMsg.textContent = err.message || err;
      errorField.appendChild(errorMsg);
    }
  },
  
  submitEmailLogInRequest(token = null) {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['X-BS-AFT'] = token;

    const button = document.querySelector('.login-button');
    button.classList.add('loading');

    const emailInput = document.getElementById('email');
    const passwordInput = document.getElementById('password');

    if (!emailInput || !passwordInput) {
      console.error('Email or password field not found');
      return false;
    }

    const params = this.getParamsFromContextData();
    params.email = emailInput.value;
    params.password = passwordInput.value;
    params.rememberMe = true;
    params.web = true;

    const url = '/api/sessions/sign_in';
    const body = JSON.stringify(params);
    const opts = { body, headers, method: 'POST' };

    fetch(url, opts)
      .then((rs) => {
        if (!rs.ok) {
          const token = rs.headers.get('X-BS-AFT');
          const delay = this.parseDelay(token);

          if (token && delay) {
            setTimeout(() => this.submitEmailLogInRequest(token), delay * 1000);
            return { stop: true };
          }
        }
        return this.checkJsonResponse(rs);
      })
      .then((rs) => {
        if (rs.stop) return;
        window.location = rs.redirect_to || '/';
      })
      .catch((err) => {
        button.classList.remove('loading');
        document.querySelector('.error-field').innerText = err;
      });
  },

  submitSocialLogInRequest(provider) {
    if (provider === 'apple') {
      AppleID.auth.signIn();
    } else if (provider === 'google') {
      const f = document.getElementById('google-form');
      if (f) {
        f.submit();
      } else {
        console.error('Could not find the login provider');
      }
    }
  },

  trackLoginInitiated() {
    const name = this.initFor();

    EventBus.publish('web:signin:dialog', {name: name}, true);

    if (!window.gtag) return;

    const params = this.getParamsFromContextData();
    const source = params?.source || null;

    gtag('event', 'login_initiated', {
      catId: params?.catId || null,
      packId: params?.packId || null,
      plan: params?.plan || null,
      send_to: GaHelper.ga_4_id(),
      source: source,
    });
  },

  validateInputCredentials() {
    const emailInput = document.getElementById('email')?.value.trim();
    const passwordInput = document.getElementById('password')?.value.trim();

    if (!(emailInput && passwordInput)) {
      return {
        isValid: false,
        identifier: null,
        message: 'Please enter an e-mail and password.',
      }
    }

    if (!StringHelper.isValidEmail(emailInput)) {
      return {
        isValid: false,
        identifier: 'email',
        message: 'Please enter a valid email address.',
      }
    }

    return {
      isValid: true,
      identifier: null,
      message: null,
    }
  },
};
